/**
 * @file Bridge通信实现文件
 * @description 实现了与Flutter通信的核心功能
 */

import { BRIDGE_HANDLER, generateCallbackId, BRIDGE_METHODS } from './constants'

/**
 * Bridge服务类
 * @class BridgeService
 * @description 实现了与Flutter通信的所有方法，使用单例模式
 */
class BridgeService {
  static instance
  /** 存储回调函数的Map */
  callbacks
  /** Bridge是否初始化完成的标志 */
  isReady
  /** 等待初始化完成的回调函数数组 */
  readyCallbacks

  /**
   * 私有构造函数，初始化属性并开始监听
   * @private
   */
  constructor() {
    this.callbacks = new Map()
    this.isReady = false
    this.readyCallbacks = []
    this.initialize()
  }

  /**
   * 获取BridgeService实例
   * @static
   * @returns {BridgeService} BridgeService的单例
   */
  static getInstance() {
    if (!BridgeService.instance) {
      BridgeService.instance = new BridgeService()
    }
    return BridgeService.instance
  }

  /**
   * 初始化Bridge服务
   * @private
   * @description 监听Flutter初始化完成事件和注册全局回调函数
   */
  initialize() {
    // 确保在 DOM 加载完成后再初始化
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupBridge())
    } else {
      this.setupBridge()
    }
  }

  /**
   * 设置 Bridge 监听和回调
   * @private
   */
  setupBridge() {
    // FIX: 偶现在main.js调用获取用户信息报错 window.fluuter_inappwebview.callHandler is not a function
    // 先检查是否已经触发过事件
    // if (window.flutter_inappwebview) {
    //   console.log('Flutter WebView 已就绪')
    //   this.isReady = true
    //   this.readyCallbacks.forEach(callback => callback())
    //   this.readyCallbacks = []
    // }

    // 监听 Flutter WebView 初始化完成事件
    window.addEventListener('flutterInAppWebViewPlatformReady', () => {
      console.log('Flutter WebView 初始化完成')
      this.isReady = true
      this.readyCallbacks.forEach(callback => callback())
      this.readyCallbacks = []
    })

    // 注册全局回调函数，供 Flutter 调用
    window.jsCallback = response => {
      const callback = this.callbacks.get(response.responseId)
      if (callback) {
        callback(response)
        this.callbacks.delete(response.responseId)
      }
    }

    // 注册全局左滑返回处理函数，供 Flutter 调用
    window.onBackPressed = () => {
      this.handleSwipeBack()
    }
  }

  /**
   * 确保Bridge已经初始化完成
   * @private
   * @returns {Promise<void>} 初始化完成的Promise
   */
  ensureReady() {
    if (this.isReady) {
      return Promise.resolve()
    }
    return new Promise(resolve => {
      this.readyCallbacks.push(resolve)
    })
  }

  /**
   * 调用无返回值的Flutter方法
   * @param {string} methodName - 方法名
   * @param {any} [data] - 可选的参数数据
   */
  async invoke(methodName, data) {
    await this.ensureReady()
    const request = {
      methodName,
      data,
    }
    window.flutter_inappwebview.callHandler(BRIDGE_HANDLER, request)
  }

  /**
   * 调用返回值的Flutter方法
   * @param {string} methodName - 方法名
   * @param {any} [data] - 可选的参数数据
   * @returns {Promise<any>} 返回Promise包装的结果
   */
  async invokeSync(methodName, data) {
    await this.ensureReady()
    const request = {
      methodName,
      data,
    }
    return window.flutter_inappwebview.callHandler(BRIDGE_HANDLER, request)
  }

  /**
   * 处理左滑返回事件
   * @private
   */
  handleSwipeBack() {
    // 获取当前路由信息
    const currentPath = window.location.pathname
    const currentHash = window.location.hash

    console.log('左滑返回触发，当前路由:', currentPath, currentHash)

    // 如果是发布页面或编辑页面，触发左上角导航的返回弹窗
    if (
      currentPath.includes('/publishPost') ||
      currentHash.includes('publishPost') ||
      currentPath.includes('/editPost') ||
      currentHash.includes('editPost') 
    ) {
      // 触发自定义事件，让Vue组件显示确认对话框
      const event = new CustomEvent('showConfirmDialog')
      document.dispatchEvent(event)
    } else if (
      currentPath.includes('/creation/detail') ||
      currentHash.includes('detail')
    ){
      // 增加图文详情路由，给客户端做右滑事件判断,当详情中调用客户端-禁止右滑方法时，禁止右滑
    

    } else {
      // 其他页面：默认返回
      this.invoke(BRIDGE_METHODS.GO_BACK)
    }
  }
}

// 导出Bridge服务实例
export const bridge = BridgeService.getInstance()
